// =================================================================================================
// React
import * as React from "react";
import _ from 'lodash';
import type { BarcodeFormat } from "@zxing/library";

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import {
    IWidgetSettingsProps, WidgetDescriptionToPlegmaThing,
    IWidgetDescription
} from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { isDefined } from '@Utils/Micro';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';

import type { BarcodeScannerPortEvent } from './BarcodeScannerWidget';
const BarcodeScanner = React.lazy(() => import("./BarcodeScannerWidget"));

// =================================================================================================
export const enum BarcodeScannerCodeFormat {
    QR = 0,
    Barcode = 1,
    DataMatrix = 2,
    Auto = 3,
    Custom = 4
}
// -------------------------------------------------------------------------------------------------
export interface BarcodeScannerData {
    scanditEnable: boolean;
    scanditLicense: string;
    scanditInitZoom: number;
    codeFormat: BarcodeScannerCodeFormat;
    hints: BarcodeFormat[] | string[];
    autoStart: boolean;
    autoSend: boolean;
    settingElement: boolean;
    width?: number;
    height?: number;
    onSuccessfullScan?: (res: BarcodeScannerPortEvent) => void;
}
// =================================================================================================
class BarcodeScannerSettings extends React.PureComponent<IWidgetSettingsProps<BarcodeScannerData>> {
    constructor(props: IWidgetSettingsProps<BarcodeScannerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<BarcodeScannerData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        dataPath: "codeFormat",
                        tooltip: 'Select the code format that we try to find.(Auto,QR,Barcode,DataMatrix)',
                        label: "Code Format",
                        items: [
                            { label: "QR", value: BarcodeScannerCodeFormat.QR },
                            { label: "Barcode", value: BarcodeScannerCodeFormat.Barcode },
                            { label: "DataMatrix", value: BarcodeScannerCodeFormat.DataMatrix },
                            { label: "Auto", value: BarcodeScannerCodeFormat.Auto },
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        dataPath: "autoStart",
                        tooltip: 'Auto start scanning without user input.',
                        label: "Auto Start"
                    },
                    {
                        type: SettingsType.Boolean,
                        dataPath: "autoSend",
                        tooltip: 'Auto send results without user input.',
                        label: "Auto Send Results"
                    }
                ]
            },
            {
                name: "Scandit",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        dataPath: "scanditEnable",
                        tooltip: 'Enable scandit library usage for scanning. You need valid license to be able to use it',
                        label: "Enable"
                    },
                    {
                        type: SettingsType.String,
                        dataPath: "scanditLicense",
                        label: "License"
                    },
                    {
                        type: SettingsType.Slider,
                        dataPath: "scanditInitZoom",
                        label: "Initial Zoom Level. 0-1",
                        maxValue: 1,
                        minValue: 0,
                        stepValue: 0.1,
                        valuePosfix: ""
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================

export class BarcodeScannerWidget extends IWidgetThingsClass<BarcodeScannerData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget>
            <BarcodeScanner />
        </WidgetsThingsStructs.LazyWidget>;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <BarcodeScannerSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: BarcodeScannerData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort({
            Name: 'Result',
            PortKey: 'result',
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: 'Json results from the scan',
            State: ''
        }, WebThingPortType.Trigger));
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as BarcodeScannerData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): BarcodeScannerData {
        return {
            scanditEnable: false,
            scanditLicense: "",
            autoStart: false,
            autoSend: false,
            codeFormat: BarcodeScannerCodeFormat.QR,
            settingElement: false,
            hints: null,
            scanditInitZoom: 0
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return BarcodeScannerWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: '1D/2D Barcode scanner',
            WidgetTitle: '',
            Key: 'BarcodeScannerWidget',
            Type: 'BarcodeScannerWidget',
            Data: BarcodeScannerWidget.GetDefaultWidgetDataStatic(),
            Description: 'BarcodeScannerWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/QR.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
}